import React, { useState, Fragment } from 'react';
import { View, StyleSheet } from 'react-native';
import { useQuery } from '@apollo/react-hooks';
import { ActivityIndicator } from 'exoflex';

import { normalizeSheetData } from '../../helpers/normalizeData';
import {
  SummaryCard,
  DateCircleSummaryCard,
  BillCard,
  ProgressCard,
  ExportCSV,
  InternalError,
  DataNotFound,
} from '../../components';
import {
  TEXT,
  PROJECT_START_COLOR,
  BLACK_OPACITY,
  SOFT_BLUE_VIOLET,
  WHITE,
  BLACK,
} from '../../constants/colors';
import { FETCH_SHEET_QUERY } from '../../graphql/queries/overview';
import { Text, ProgressBar, Divider, Announcement, Icon } from '../../core-ui';
import { FetchSheetQuery } from '../../generated/FetchSheetQuery';

export default function OverviewWrapper() {
  let { loading, data, error } = useQuery<FetchSheetQuery>(FETCH_SHEET_QUERY, {
    fetchPolicy: 'network-only',
  });

  let [next, isNextPressed] = useState(false);

  if (loading) {
    return (
      <View style={styles.activityIndicatorStyle}>
        <ActivityIndicator
          size="large"
          color={SOFT_BLUE_VIOLET}
          animating={true}
        />
      </View>
    );
  }

  if (error) {
    return <InternalError errorMessage={error.message} />;
  }

  if (!data) {
    return null;
  }

  //special case for dbo-fetchsheet
  if (
    data.fetchSheet.originalFeatures.totalDays === 0 &&
    data.fetchSheet.sheetId != '1hjB0jHp5DpxizegmPlS5DhuO3QXDwGVCf900T9JpmJQ'
  ) {
    return <DataNotFound />;
  }

  let isSpecialCase =
    data.fetchSheet.sheetId === '1hjB0jHp5DpxizegmPlS5DhuO3QXDwGVCf900T9JpmJQ'
      ? true
      : false;

  let normalizedData = normalizeSheetData(data);
  let projectDaysRemaining = normalizedData.projectDaysRemaining.toString();

  let DBOCard = () => {
    if (data) {
      return (
        <Fragment>
          <View style={styles.DeliveryDateDBO}>
            <SummaryCard
              leftComponent={
                <DateCircleSummaryCard
                  numDay={normalizedData.deliveryDate}
                  descText={normalizedData.deliveryMonthYearDesc}
                />
              }
              rightText={`Est. Delivery \nDate`}
            />
          </View>
          <View style={styles.summaryCardContainer}>
            <SummaryCard
              leftComponent={
                <DateCircleSummaryCard
                  numDay={
                    data.fetchSheet.estimatedMandaysRemaining >= 0
                      ? String(data.fetchSheet.estimatedMandaysRemaining)
                      : null
                  }
                  descText={
                    data.fetchSheet.estimatedMandaysRemaining >= 0
                      ? 'days'
                      : 'Over\nProjections'
                  }
                />
              }
              rightText={`Est. Mandays \nRemaining`}
            />
          </View>
          <View style={styles.MandaysRemainingDBO}>
            <SummaryCard
              leftComponent={
                <DateCircleSummaryCard
                  numDay={normalizedData.totalMandays.toString()}
                  descText={'days'}
                />
              }
              rightText={`Current Mandays \nSpent`}
            />
          </View>
        </Fragment>
      );
    } else {
      return null;
    }
  };

  let NormalCard = () => {
    if (data) {
      if (next) {
        return (
          <Fragment>
            <Icon
              name="chevron-left"
              iconStyle={{ paddingTop: 45 }}
              onPress={() => {
                isNextPressed(false);
              }}
              disabled={!next}
              color={next ? BLACK : WHITE}
            />
            <View style={styles.summaryCardContainer}>
              <SummaryCard
                leftComponent={
                  <DateCircleSummaryCard
                    numDay={normalizedData.deliveryDate}
                    descText={normalizedData.deliveryMonthYearDesc}
                  />
                }
                rightText={`Est. UAT Start \nDate`}
              />
            </View>

            <View style={styles.summaryCardContainer}>
              <SummaryCard
                leftComponent={
                  <DateCircleSummaryCard
                    numDay={
                      data.fetchSheet.estimatedMandaysRemaining >= 0
                        ? String(data.fetchSheet.estimatedMandaysRemaining)
                        : null
                    }
                    descText={
                      data.fetchSheet.estimatedMandaysRemaining >= 0
                        ? 'days'
                        : 'Over\nProjections'
                    }
                  />
                }
                rightText={`Est. Mandays \nRemaining`}
              />
            </View>
            <View style={styles.flex}>
              <SummaryCard
                leftComponent={
                  <DateCircleSummaryCard
                    numDay={projectDaysRemaining}
                    descText={'days'}
                  />
                }
                rightText={`Project Days \nRemaining`}
              />
            </View>
            <Icon
              name="chevron-right"
              iconStyle={{ paddingTop: 45 }}
              onPress={() => {
                isNextPressed(true);
              }}
              disabled={next}
              color={next ? WHITE : BLACK}
            />
          </Fragment>
        );
      } else {
        return (
          <Fragment>
            <Icon
              name="chevron-left"
              iconStyle={{ paddingTop: 45 }}
              onPress={() => {
                isNextPressed(false);
              }}
              disabled={!next}
              color={next ? BLACK : WHITE}
            />
            <View style={styles.summaryCardContainer}>
              <SummaryCard
                leftComponent={
                  <ProgressBar
                    type="circular"
                    progress={data.fetchSheet.estimatedProgress}
                    width={84}
                    textStyle={styles.progressBarTextStyle}
                    backgroundColor={BLACK_OPACITY}
                  />
                }
                rightText={`Estimated \nProgress`}
              />
            </View>

            <View style={styles.summaryCardContainer}>
              <SummaryCard
                leftComponent={
                  <DateCircleSummaryCard
                    numDay={normalizedData.deliveryDate}
                    descText={normalizedData.deliveryMonthYearDesc}
                  />
                }
                rightText={`Est. UAT Start \nDate`}
              />
            </View>
            <View style={styles.flex}>
              <SummaryCard
                leftComponent={
                  <DateCircleSummaryCard
                    numDay={
                      data.fetchSheet.estimatedMandaysRemaining >= 0
                        ? String(data.fetchSheet.estimatedMandaysRemaining)
                        : null
                    }
                    descText={
                      data.fetchSheet.estimatedMandaysRemaining >= 0
                        ? 'days'
                        : 'Over\nProjections'
                    }
                  />
                }
                rightText={`Est. Mandays \nRemaining`}
              />
            </View>
            <Icon
              name="chevron-right"
              iconStyle={{ paddingTop: 45 }}
              onPress={() => {
                isNextPressed(true);
              }}
              disabled={next}
              color={next ? WHITE : BLACK}
            />
          </Fragment>
        );
      }
    } else {
      return null;
    }
  };

  return (
    <View style={styles.container}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          marginBottom: 24,
        }}
      >
        <View style={{ justifyContent: 'center' }}>
          <Text weight="medium" style={styles.projectNameText}>
            {normalizedData.projectName}
          </Text>
          <Text weight="normal" style={styles.startDateText}>
            {'Started development at ' +
              normalizedData.startMonth +
              ' ' +
              normalizedData.startDate +
              ', ' +
              normalizedData.startYear}
          </Text>
        </View>
        <View
          style={{
            justifyContent: 'center',
          }}
        >
          <ExportCSV isDBO={isSpecialCase} />
        </View>
      </View>

      <Divider style={styles.divider} />

      {data.fetchSheet.estimatedMandaysRemaining < 0 && (
        <Announcement
          message="We have passed the est. Total Mondays on the original estimate due to several factors. Please contact your Project Manager
for further details"
          style={styles.announcement}
        />
      )}

      <View style={{ marginLeft: -33, marginRight: -33 }}>
        <View style={styles.headerTextContainer}>
          <Text weight="medium" style={styles.headerText}>
            Summary
          </Text>
        </View>
        <View style={styles.rowCard}>
          {isSpecialCase ? <DBOCard /> : <NormalCard />}
        </View>
      </View>
      <View style={styles.detailContainer}>
        <View style={styles.rowCard}>
          <View style={styles.progressCardContainer}>
            <ProgressCard
              stackBarData={normalizedData.barChartData}
              popoverData={normalizedData.popoverData}
              estimatedBreakdown={normalizedData.totalMandays}
              isSpecialCase={isSpecialCase}
            />
          </View>
          <BillCard data={normalizedData.billData} />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  rowCard: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  summaryCardContainer: {
    flex: 1,
    marginRight: 24,
  },
  DeliveryDateDBO: {
    flex: 1,
    paddingLeft: 34,
    marginRight: 24,
  },
  MandaysRemainingDBO: {
    flex: 1,
    paddingRight: 30,
  },
  headerTextContainer: {
    marginBottom: 24,
    marginLeft: 34,
  },
  projectNameText: {
    fontSize: 24,
    marginBottom: 8,
  },
  startDateText: {
    fontSize: 14,
    color: PROJECT_START_COLOR,
  },
  headerText: {
    fontSize: 18,
  },
  divider: {
    marginBottom: 24,
    zIndex: -1,
  },
  detailContainer: {
    marginVertical: 24,
  },
  progressCardContainer: {
    flex: 1,
    marginRight: 24,
    zIndex: 1,
  },
  progressBarTextStyle: {
    fontSize: 20,
    color: TEXT.white,
  },
  activityIndicatorStyle: {
    flex: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  announcement: {
    marginBottom: 24,
  },
});
